import React, { useState, useEffect } from 'react';
import moment from 'moment';
import { 
  setupPDF, 
  getPageDimensions, 
  addHeader, 
  addFooter,
  addPaymentInfo,
  drawSummaryBox,
  colors,
  savePDF,
  autoTable
} from '../utils/pdfTemplate';

function GigsList({ gigs, onSelectGig, onEditGig, onDeleteGig, onNewGig, title, initialFilters, selectedGig, user }) {
  const [sortBy, setSortBy] = useState('start_date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [filterClient, setFilterClient] = useState('');
  const [filterLocation, setFilterLocation] = useState('');
  const [filterPaid, setFilterPaid] = useState(''); // 'all', 'paid', 'unpaid'
  const [filterGigType, setFilterGigType] = useState(''); // 'all', 'mine', 'substitution'

  // Apply initial filters if provided
  useEffect(() => {
    if (initialFilters) {
      if (initialFilters.filterPaid !== undefined) {
        setFilterPaid(initialFilters.filterPaid);
      }
      if (initialFilters.filterGigType !== undefined) {
        setFilterGigType(initialFilters.filterGigType);
      }
      if (initialFilters.filterClient !== undefined) {
        setFilterClient(initialFilters.filterClient);
      }
      if (initialFilters.filterLocation !== undefined) {
        setFilterLocation(initialFilters.filterLocation);
      }
    }
  }, [initialFilters]);

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  // Get unique clients and locations for filters
  const uniqueClients = [...new Set(gigs.map(gig => gig.client_name).filter(Boolean))].sort();
  const uniqueLocations = [...new Set(gigs.map(gig => gig.venue).filter(Boolean))].sort();

  // Filter gigs
  const today = moment().startOf('day');
  const filteredGigs = gigs.filter(gig => {
    const matchesClient = !filterClient || gig.client_name === filterClient;
    const matchesLocation = !filterLocation || gig.venue === filterLocation;
    const matchesPaid = !filterPaid || filterPaid === 'all' || 
      (filterPaid === 'paid' && (gig.paid === 1 || gig.paid === true)) ||
      (filterPaid === 'unpaid' && (gig.paid === 0 || gig.paid === false || !gig.paid));
    // Determine gig type for filtering
    const gigType = gig.gig_type || (gig.is_substitution === 1 || gig.is_substitution === true ? 'sub_for_other' : 'mine');
    const matchesGigType = !filterGigType || filterGigType === 'all' ||
      (filterGigType === 'mine' && gigType === 'mine') ||
      (filterGigType === 'sub_for_other' && gigType === 'sub_for_other') ||
      (filterGigType === 'sub_for_me' && gigType === 'sub_for_me');
    
    // When filtering for unpaid gigs, only include gigs that have ended (end_date has passed)
    const hasEnded = gig.end_date && moment(gig.end_date).startOf('day').isBefore(today);
    const excludeFutureUnpaid = filterPaid === 'unpaid' && !hasEnded;
    
    // When filtering for unpaid gigs (but not specifically for subs), exclude canceled gigs
    // This matches the behavior of the original "Unpaid Gigs" page
    const isCanceled = gig.is_canceled === 1 || gig.is_canceled === true;
    const excludeCanceled = filterPaid === 'unpaid' && filterGigType !== 'sub_for_me';
    
    // For unpaid subs, exclude those marked as substitute_not_paid
    const substituteNotPaid = gig.substitute_not_paid === 1 || gig.substitute_not_paid === true;
    const excludeSubNotPaid = filterPaid === 'unpaid' && filterGigType === 'sub_for_me' && substituteNotPaid;
    
    return matchesClient && matchesLocation && matchesPaid && matchesGigType && 
           !excludeFutureUnpaid && !(excludeCanceled && isCanceled) && !excludeSubNotPaid;
  });

  const sortedGigs = [...filteredGigs].sort((a, b) => {
    let aValue, bValue;
    
    switch (sortBy) {
      case 'title':
        aValue = a.title.toLowerCase();
        bValue = b.title.toLowerCase();
        break;
      case 'client':
        aValue = (a.client_name || '').toLowerCase();
        bValue = (b.client_name || '').toLowerCase();
        break;
      case 'start_date':
        aValue = new Date(a.start_date);
        bValue = new Date(b.start_date);
        break;
      case 'total_revenue':
        const daysA = moment(a.end_date).diff(moment(a.start_date), 'days') + 1;
        const baseRevenueA = (a.rate_type === 'flat' ? a.daily_rate : daysA * a.daily_rate);
        const additionalChargesA = a.additional_charges?.reduce((sum, charge) => sum + parseFloat(charge.amount), 0) || 0;
        const revenueA = baseRevenueA + additionalChargesA;
        const daysB = moment(b.end_date).diff(moment(b.start_date), 'days') + 1;
        const baseRevenueB = (b.rate_type === 'flat' ? b.daily_rate : daysB * b.daily_rate);
        const additionalChargesB = b.additional_charges?.reduce((sum, charge) => sum + parseFloat(charge.amount), 0) || 0;
        const revenueB = baseRevenueB + additionalChargesB;
        aValue = revenueA;
        bValue = revenueB;
        break;
      default:
        aValue = a[sortBy];
        bValue = b[sortBy];
    }

    if (typeof aValue === 'string') {
      return sortOrder === 'asc' 
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    } else {
      return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
    }
  });

  const exportToPDF = () => {
    // Check if this is an Unpaid Subs report
    const isUnpaidSubsReport = filterGigType === 'sub_for_me';
    
    if (isUnpaidSubsReport) {
      // Generate Unpaid Subs specific report
      exportUnpaidSubsPDF();
      return;
    }
    
    const doc = setupPDF('landscape');
    const { pageHeight } = getPageDimensions(doc);
    
    // Header
    addHeader(doc, 'GIGS REPORT', 'Financial Overview', `Generated: ${moment().format('MMMM D, YYYY')}`);
    
    // Calculate totals
    const totalRevenue = sortedGigs.reduce((sum, gig) => {
      const days = moment(gig.end_date).diff(moment(gig.start_date), 'days') + 1;
      const baseRevenue = gig.rate_type === 'flat' ? parseFloat(gig.daily_rate) : days * parseFloat(gig.daily_rate);
      const additionalCharges = gig.additional_charges?.reduce((s, charge) => s + parseFloat(charge.amount), 0) || 0;
      return sum + baseRevenue + additionalCharges;
    }, 0);
    
    const totalExpenses = sortedGigs.reduce((sum, gig) => {
      return sum + (gig.expenses?.reduce((s, exp) => s + parseFloat(exp.amount), 0) || 0);
    }, 0);
    
    const totalSelfExpenses = sortedGigs.reduce((sum, gig) => {
      return sum + (gig.expenses?.filter(exp => exp.covered_by === 'self').reduce((s, exp) => s + parseFloat(exp.amount), 0) || 0);
    }, 0);
    
    // Calculate total substitute payments (for sub_for_me gigs)
    const totalSubstitutePayments = sortedGigs.reduce((sum, gig) => {
      const gigType = gig.gig_type || (gig.is_substitution === 1 || gig.is_substitution === true ? 'sub_for_other' : 'mine');
      if (gigType === 'sub_for_me' && gig.substitute_amount) {
        return sum + parseFloat(gig.substitute_amount);
      }
      return sum;
    }, 0);
    
    // Calculate VAT if user is regular VAT
    const userVatStatus = user?.vat_status;
    const vatBase = totalRevenue; // VAT base is revenue (base + additional charges, excluding expenses)
    const totalVat = userVatStatus === 'regular' ? vatBase * 0.23 : 0;
    const totalRevenueWithVat = totalRevenue + totalVat;
    
    const paidGigs = sortedGigs.filter(g => g.paid === 1 || g.paid === true).length;
    const totalProfit = totalRevenueWithVat - totalSelfExpenses - totalSubstitutePayments;
    
    // Summary boxes using template helper
    const boxY = 30;
    const boxHeight = 18;
    const boxWidth = 52;
    const boxGap = 6;
    let boxX = 14;
    
    // Local box helper (uses template colors)
    const drawStatBox = (label, value, accentColor, x) => {
      doc.setFillColor(...colors.bgLight);
      doc.roundedRect(x, boxY, boxWidth, boxHeight, 2, 2, 'F');
      doc.setFillColor(...accentColor);
      doc.rect(x, boxY, 3, boxHeight, 'F');
      doc.setFontSize(7);
      doc.setTextColor(...colors.lightGray);
      doc.text(label, x + 7, boxY + 6);
      doc.setFontSize(11);
      doc.setTextColor(...colors.darkGray);
      doc.setFont('helvetica', 'bold');
      doc.text(value, x + 7, boxY + 14);
      doc.setFont('helvetica', 'normal');
    };
    
    drawStatBox('TOTAL GIGS', String(sortedGigs.length), colors.yellow, boxX);
    boxX += boxWidth + boxGap;
    if (userVatStatus === 'regular' && totalVat > 0) {
      drawStatBox('REVENUE', `€${totalRevenueWithVat.toFixed(0)}`, colors.green, boxX);
      boxX += boxWidth + boxGap;
      drawStatBox('VAT (23%)', `€${totalVat.toFixed(0)}`, colors.blue, boxX);
      boxX += boxWidth + boxGap;
      drawStatBox('EXPENSES', `€${totalExpenses.toFixed(0)}`, colors.orange, boxX);
    } else {
      drawStatBox('REVENUE', `€${totalRevenue.toFixed(0)}`, colors.green, boxX);
      boxX += boxWidth + boxGap;
      drawStatBox('EXPENSES', `€${totalExpenses.toFixed(0)}`, colors.orange, boxX);
    }
    boxX += boxWidth + boxGap;
    drawStatBox('NET PROFIT', `€${totalProfit.toFixed(0)}`, colors.green, boxX);
    boxX += boxWidth + boxGap;
    drawStatBox('PAID', String(paidGigs), colors.green, boxX);
    
    // Show VAT Exempt notation if applicable
    if (userVatStatus === 'exempt') {
      doc.setFontSize(7);
      doc.setTextColor(...colors.lightGray);
      doc.text('VAT Exempt', 14, boxY + boxHeight + 4);
    }
    
    // Filters info
    let startY = 58;
    const hasActiveFilters = filterClient || filterLocation || filterPaid || filterGigType;
    if (hasActiveFilters) {
      doc.setFontSize(8);
      doc.setTextColor(...colors.lightGray);
      let filterText = 'Active Filters: ';
      const filters = [];
      if (filterClient) filters.push(`Client: ${filterClient}`);
      if (filterLocation) filters.push(`Location: ${filterLocation}`);
      if (filterPaid && filterPaid !== 'all') filters.push(`Payment: ${filterPaid === 'paid' ? 'Paid' : 'Unpaid'}`);
      if (filterGigType && filterGigType !== 'all') {
        let typeLabel = 'All';
        if (filterGigType === 'mine') typeLabel = 'My Gigs';
        else if (filterGigType === 'sub_for_other') typeLabel = 'Substituting';
        else if (filterGigType === 'sub_for_me') typeLabel = 'Sub for Me';
        filters.push(`Type: ${typeLabel}`);
      }
      filterText += filters.join('  |  ');
      doc.text(filterText, 14, 55);
      startY = 62;
    }

    // ============================================
    // TABLE DATA
    // ============================================
    const tableData = sortedGigs.map(gig => {
      const days = moment(gig.end_date).diff(moment(gig.start_date), 'days') + 1;
      const baseRevenue = gig.rate_type === 'flat' ? parseFloat(gig.daily_rate) : days * parseFloat(gig.daily_rate);
      const additionalCharges = gig.additional_charges?.reduce((sum, charge) => sum + parseFloat(charge.amount), 0) || 0;
      const revenue = baseRevenue + additionalCharges;
      // Calculate VAT for this gig if regular VAT
      const gigVatBase = revenue;
      const gigVat = userVatStatus === 'regular' ? gigVatBase * 0.23 : 0;
      const revenueWithVat = revenue + gigVat;
      const selfExpenses = gig.expenses?.filter(exp => exp.covered_by === 'self').reduce((sum, exp) => sum + parseFloat(exp.amount), 0) || 0;
      const gigType = gig.gig_type || (gig.is_substitution === 1 || gig.is_substitution === true ? 'sub_for_other' : 'mine');
      // For sub_for_me gigs, subtract the substitute amount from profit
      const substituteAmount = (gigType === 'sub_for_me' && gig.substitute_amount) ? parseFloat(gig.substitute_amount) : 0;
      const profit = revenueWithVat - selfExpenses - substituteAmount;
      const isPaid = gig.paid === 1 || gig.paid === true;
      
      let typeText = '';
      if (gigType === 'sub_for_other') typeText = 'SUB';
      else if (gigType === 'sub_for_me') typeText = `SUBBED€${substituteAmount.toFixed(0)}`;
      
      return [
        gig.title || '',
        gig.client_name || '-',
        gig.venue || '-',
        moment(gig.start_date).format('DD/MM/YY'),
        days === 1 ? '1 day' : `${days} days`,
        userVatStatus === 'regular' ? `€${revenueWithVat.toFixed(0)}` : `€${revenue.toFixed(0)}`,
        `€${profit.toFixed(0)}`,
        isPaid ? 'PAID' : 'UNPAID',
        typeText
      ];
    });

    // ============================================
    // TABLE
    // ============================================
    
    autoTable(doc, {
      startY: startY,
      head: [['Title', 'Client', 'Venue', 'Date', 'Duration', 'Revenue', 'Profit', 'Status', 'Type']],
      body: tableData,
      theme: 'plain',
      headStyles: { 
        fillColor: [212, 160, 23],
        textColor: [255, 255, 255], 
        fontStyle: 'bold',
        fontSize: 8,
        cellPadding: 4
      },
      bodyStyles: { 
        textColor: [51, 51, 51],
        fontSize: 8,
        cellPadding: 3
      },
      alternateRowStyles: { 
        fillColor: [250, 250, 250]
      },
      styles: { 
        fontSize: 8, 
        overflow: 'ellipsize',
        cellWidth: 'wrap',
        lineColor: [230, 230, 230],
        lineWidth: 0.1
      },
      margin: { left: 14, right: 14, bottom: 25 }, // Add bottom margin for footer
      columnStyles: {
        0: { cellWidth: 45 }, // Title
        1: { cellWidth: 35 }, // Client
        2: { cellWidth: 35 }, // Venue
        3: { cellWidth: 22 }, // Date
        4: { cellWidth: 20 }, // Duration
        5: { halign: 'right', cellWidth: 22 }, // Revenue
        6: { halign: 'right', cellWidth: 22 }, // Profit
        7: { halign: 'center', cellWidth: 18 }, // Status
        8: { halign: 'center', cellWidth: 18 }  // Type
      },
      didParseCell: function (data) {
        // Style payment status
        if (data.section === 'body' && data.column.index === 7) {
          if (data.cell.raw === 'PAID') {
            data.cell.styles.textColor = [34, 134, 58];
            data.cell.styles.fontStyle = 'bold';
          } else if (data.cell.raw === 'UNPAID') {
            data.cell.styles.textColor = [203, 104, 0];
            data.cell.styles.fontStyle = 'bold';
          }
        }
        // Style type column
        if (data.section === 'body' && data.column.index === 8) {
          if (data.cell.raw === 'SUB' || data.cell.raw === 'SUBBED') {
            data.cell.styles.textColor = [37, 99, 235];
            data.cell.styles.fontStyle = 'bold';
          }
        }
      },
      didDrawPage: function (data) {
        // Add footer to each page
        addFooter(doc, { username: user?.username || 'Unknown' });
      }
    });

    // Payment info (IBAN) - place after table, only on last page
    const finalY = doc.lastAutoTable.finalY || startY;
    const ibanHeight = 30;
    
    // Check if we have space for IBAN on current page
    if (user?.iban && user.iban.trim() !== '') {
      if (finalY + ibanHeight > pageHeight - 25) {
        // Not enough space, add new page
        doc.addPage();
        addPaymentInfo(doc, user.iban, 20);
        addFooter(doc, { username: user?.username || 'Unknown' });
      } else {
        // Enough space, add IBAN after table
        addPaymentInfo(doc, user.iban, finalY + 8);
      }
    }

    savePDF(doc, `gigs-report-${moment().format('YYYY-MM-DD')}.pdf`);
  };

  // ============================================
  // UNPAID SUBS PDF - What I owe to substitutes
  // ============================================
  const exportUnpaidSubsPDF = () => {
    const doc = setupPDF('portrait');
    
    // Header
    addHeader(doc, 'OUTSTANDING PAYMENTS', 'Amounts Owed to Substitutes', `Generated: ${moment().format('D/M/YYYY')}`);
    
    // Filter for sub_for_me gigs where substitute hasn't been paid
    const unpaidSubGigs = sortedGigs.filter(gig => {
      const gigType = gig.gig_type || (gig.is_substitution === 1 || gig.is_substitution === true ? 'sub_for_other' : 'mine');
      const substitutePaid = gig.substitute_paid === 1 || gig.substitute_paid === true;
      return gigType === 'sub_for_me' && !substitutePaid && gig.substitute_amount > 0;
    });
    
    // Calculate total owed
    const totalOwed = unpaidSubGigs.reduce((sum, gig) => sum + parseFloat(gig.substitute_amount || 0), 0);
    
    // Summary box
    let y = 35;
    doc.setFillColor(255, 240, 220);
    doc.roundedRect(14, y, 180, 20, 2, 2, 'F');
    doc.setFillColor(255, 152, 0);
    doc.rect(14, y, 4, 20, 'F');
    
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text('TOTAL OUTSTANDING', 22, y + 7);
    doc.setFontSize(16);
    doc.setTextColor(255, 87, 34);
    doc.setFont('helvetica', 'bold');
    doc.text(`€${totalOwed.toFixed(2)}`, 22, y + 16);
    doc.setFont('helvetica', 'normal');
    
    doc.setFontSize(9);
    doc.setTextColor(100, 100, 100);
    doc.text(`${unpaidSubGigs.length} unpaid substitute${unpaidSubGigs.length !== 1 ? 's' : ''}`, 100, y + 12);
    
    y += 30;
    
    // Table
    const tableData = unpaidSubGigs.map(gig => {
      const subCharge = gig.additional_charges?.find(c => c.charge_type === 'sub');
      const subName = subCharge?.substitute_name || gig.substitute_name || 'Unknown';
      const amount = parseFloat(gig.substitute_amount || 0);
      
      return [
        moment(gig.start_date).format('D/M/YY'),
        gig.title || '-',
        subName,
        gig.venue || '-',
        `€${amount.toFixed(2)}`
      ];
    });
    
    autoTable(doc, {
      startY: y,
      head: [['Date', 'Gig', 'Substitute', 'Venue', 'Amount Owed']],
      body: tableData,
      theme: 'striped',
      headStyles: { 
        fillColor: [255, 152, 0],
        textColor: [255, 255, 255], 
        fontStyle: 'bold',
        fontSize: 9
      },
      bodyStyles: {
        fontSize: 9
      },
      columnStyles: {
        0: { cellWidth: 22 },
        1: { cellWidth: 50 },
        2: { cellWidth: 40 },
        3: { cellWidth: 45 },
        4: { cellWidth: 28, halign: 'right', fontStyle: 'bold', textColor: [255, 87, 34] }
      },
      foot: [[
        '', '', '', 'TOTAL:',
        `€${totalOwed.toFixed(2)}`
      ]],
      footStyles: {
        fillColor: [50, 50, 50],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 10
      }
    });
    
    // Footer with IBAN if available
    if (user?.iban) {
      const finalY = doc.lastAutoTable?.finalY || y + 50;
      addPaymentInfo(doc, user.iban, finalY + 10);
    }
    
    addFooter(doc, { username: user?.username || 'Unknown' });
    
    savePDF(doc, `unpaid-subs-${moment().format('YYYY-MM-DD')}.pdf`);
  };

  // ============================================
  // CLIENT STATEMENT PDF
  // ============================================
  const exportClientStatement = () => {
    if (!filterClient) return;
    
    // Get unpaid gigs for this client
    const today = moment().startOf('day');
    const clientUnpaidGigs = gigs.filter(gig => {
      const matchesClient = gig.client_name === filterClient;
      const isUnpaid = !(gig.paid === 1 || gig.paid === true);
      const hasEnded = gig.end_date && moment(gig.end_date).startOf('day').isBefore(today);
      const isCanceled = gig.is_canceled === 1 || gig.is_canceled === true;
      return matchesClient && isUnpaid && hasEnded && !isCanceled;
    });
    
    if (clientUnpaidGigs.length === 0) {
      alert('No unpaid gigs found for this client.');
      return;
    }
    
    // Get the company name (long name) and VAT ID from the first gig
    const clientCompany = clientUnpaidGigs[0]?.client_company || filterClient;
    const clientVatId = clientUnpaidGigs[0]?.client_vat_id;
    
    const doc = setupPDF('portrait');
    const { pageWidth, pageHeight, margin, contentWidth } = getPageDimensions(doc);
    
    // Header (using template)
    addHeader(doc, 'PAYMENT STATEMENT', 'Outstanding Balance', `Date: ${moment().format('MMMM D, YYYY')}`);
    
    // Get user VAT status
    const userVatStatus = user?.vat_status;
    
    // Calculate totals
    let totalServices = 0;
    let totalExpenses = 0;
    let totalPaid = 0;
    
    const tableData = clientUnpaidGigs.map(gig => {
      const days = moment(gig.end_date).diff(moment(gig.start_date), 'days') + 1;
      const baseRevenue = gig.rate_type === 'flat' ? parseFloat(gig.daily_rate) : days * parseFloat(gig.daily_rate);
      const additionalCharges = gig.additional_charges?.filter(c => c.charge_type !== 'sub').reduce((sum, charge) => sum + parseFloat(charge.amount), 0) || 0;
      const companyExpenses = gig.expenses?.filter(exp => exp.covered_by === 'company').reduce((sum, exp) => sum + parseFloat(exp.amount), 0) || 0;
      const serviceTotal = baseRevenue + additionalCharges;
      // Calculate VAT for this gig if regular VAT
      const gigVatBase = serviceTotal;
      const gigVat = userVatStatus === 'regular' ? gigVatBase * 0.23 : 0;
      const serviceTotalWithVat = serviceTotal + gigVat;
      const gigTotal = serviceTotalWithVat + companyExpenses;
      const paidAmount = parseFloat(gig.paid_amount) || 0;
      
      totalServices += serviceTotalWithVat;
      totalExpenses += companyExpenses;
      totalPaid += paidAmount;
      
      return {
        date: moment(gig.start_date).format('DD/MM/YY'),
        title: gig.title,
        venue: gig.venue || '-',
        services: serviceTotalWithVat,
        expenses: companyExpenses,
        paid: paidAmount,
        balance: gigTotal - paidAmount,
        vat: gigVat
      };
    });
    
    // Calculate total VAT
    const totalVat = userVatStatus === 'regular' 
      ? clientUnpaidGigs.reduce((sum, gig) => {
          const days = moment(gig.end_date).diff(moment(gig.start_date), 'days') + 1;
          const baseRevenue = gig.rate_type === 'flat' ? parseFloat(gig.daily_rate) : days * parseFloat(gig.daily_rate);
          const additionalCharges = gig.additional_charges?.filter(c => c.charge_type !== 'sub').reduce((sum, charge) => sum + parseFloat(charge.amount), 0) || 0;
          const gigVatBase = baseRevenue + additionalCharges;
          return sum + (gigVatBase * 0.23);
        }, 0)
      : 0;
    
    const totalOwed = totalServices + totalExpenses - totalPaid;
    
    // Client info box (left) - make taller if VAT ID present
    const clientBoxHeight = clientVatId ? 28 : 22;
    doc.setFillColor(...colors.bgLight);
    doc.roundedRect(margin, 36, contentWidth * 0.55, clientBoxHeight, 2, 2, 'F');
    
    doc.setFontSize(8);
    doc.setTextColor(...colors.lightGray);
    doc.text('BILL TO', margin + 8, 45);
    
    doc.setFontSize(12);
    doc.setTextColor(...colors.darkGray);
    doc.setFont('helvetica', 'bold');
    doc.text(clientCompany, margin + 8, 53);
    doc.setFont('helvetica', 'normal');
    
    if (clientVatId) {
      doc.setFontSize(8);
      doc.setTextColor(...colors.mediumGray);
      doc.text(`VAT: ${clientVatId}`, margin + 8, 60);
    }
    
    // Amount due box (right) - match height with client box
    doc.setFillColor(...colors.orange);
    doc.roundedRect(margin + contentWidth * 0.58, 36, contentWidth * 0.42, clientBoxHeight, 2, 2, 'F');
    
    doc.setFontSize(8);
    doc.setTextColor(...colors.white);
    doc.text('TOTAL AMOUNT DUE', margin + contentWidth * 0.58 + 8, 45);
    
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text(`€${totalOwed.toFixed(2)}`, pageWidth - margin - 8, 53, { align: 'right' });
    doc.setFont('helvetica', 'normal');
    
    // Table
    const tableBody = tableData.map(row => [
      row.date,
      row.title,
      `€${row.services.toFixed(2)}`,
      row.expenses > 0 ? `€${row.expenses.toFixed(2)}` : '-',
      row.paid > 0 ? `€${row.paid.toFixed(2)}` : '-',
      `€${row.balance.toFixed(2)}`
    ]);
    
    autoTable(doc, {
      startY: clientVatId ? 72 : 66,
      head: [['Date', 'Description', 'Services', 'Expenses', 'Paid', 'Balance']],
      body: tableBody,
      theme: 'plain',
      headStyles: { 
        fillColor: colors.yellow,
        textColor: colors.white, 
        fontStyle: 'bold',
        fontSize: 8,
        cellPadding: 4,
        halign: 'center'
      },
      bodyStyles: { 
        textColor: colors.darkGray,
        fontSize: 8,
        cellPadding: 3
      },
      alternateRowStyles: { 
        fillColor: colors.bgAlt
      },
      styles: { 
        fontSize: 8, 
        overflow: 'ellipsize',
        lineColor: [240, 240, 240],
        lineWidth: 0.1
      },
      margin: { left: margin, right: margin, bottom: 25 },
      tableWidth: contentWidth,
      columnStyles: {
        0: { cellWidth: 20, halign: 'center' },
        1: { cellWidth: 'auto' },
        2: { cellWidth: 24, halign: 'right' },
        3: { cellWidth: 24, halign: 'right' },
        4: { cellWidth: 20, halign: 'right' },
        5: { cellWidth: 24, halign: 'right', fontStyle: 'bold' }
      },
      didDrawPage: function () {
        // Add footer to each page
        addFooter(doc, { username: user?.username || 'Unknown' });
      }
    });
    
    // Summary section - check if we need a new page
    let summaryY = doc.lastAutoTable.finalY + 8;
    const summaryHeight = 100; // Approximate height needed for summary + info + IBAN
    
    if (summaryY + summaryHeight > pageHeight - 25) {
      // Not enough space, add new page
      doc.addPage();
      addFooter(doc, { username: user?.username || 'Unknown' });
      summaryY = 20;
    }
    
    drawSummaryBox(doc, margin, summaryY, contentWidth, 52, colors.yellow);
    
    summaryY += 10;
    const col1 = margin + 12;
    const col2 = pageWidth - margin - 10;
    
    doc.setFontSize(9);
    doc.setTextColor(...colors.darkGray);
    // Calculate base services (without VAT) for display
    const baseServices = totalServices - totalVat;
    if (userVatStatus === 'regular' && totalVat > 0) {
      doc.text('Services & Fees', col1, summaryY);
      doc.text(`€${baseServices.toFixed(2)}`, col2, summaryY, { align: 'right' });
      
      summaryY += 10;
      doc.text('VAT (23%)', col1, summaryY);
      doc.text(`€${totalVat.toFixed(2)}`, col2, summaryY, { align: 'right' });
    } else {
      doc.text('Services & Fees', col1, summaryY);
      doc.text(`€${baseServices.toFixed(2)}`, col2, summaryY, { align: 'right' });
    }
    
    if (userVatStatus === 'exempt') {
      summaryY += 10;
      doc.setFontSize(8);
      doc.setTextColor(...colors.lightGray);
      doc.text('VAT Exempt', col1, summaryY);
    }
    
    summaryY += 10;
    doc.setFontSize(9);
    doc.setTextColor(...colors.lightGray);
    doc.text('Reimbursable Expenses', col1, summaryY);
    doc.text(`€${totalExpenses.toFixed(2)}`, col2, summaryY, { align: 'right' });
    
    if (totalPaid > 0) {
      summaryY += 10;
      doc.setTextColor(...colors.green);
      doc.text('Amount Already Paid', col1, summaryY);
      doc.text(`- €${totalPaid.toFixed(2)}`, col2, summaryY, { align: 'right' });
    }
    
    summaryY += 8;
    doc.setDrawColor(220, 220, 220);
    doc.line(col1, summaryY, col2, summaryY);
    
    summaryY += 10;
    doc.setFontSize(11);
    doc.setTextColor(...colors.darkGray);
    doc.setFont('helvetica', 'bold');
    doc.text(userVatStatus === 'regular' ? 'TOTAL DUE (incl. VAT)' : 'TOTAL DUE', col1, summaryY);
    doc.setTextColor(...colors.orange);
    doc.text(`€${totalOwed.toFixed(2)}`, col2, summaryY, { align: 'right' });
    doc.setFont('helvetica', 'normal');
    
    // Payment info text
    const infoY = summaryY + 20;
    doc.setFontSize(8);
    doc.setTextColor(...colors.lightGray);
    doc.text('Please remit payment at your earliest convenience.', margin, infoY);
    doc.text(`This statement includes ${clientUnpaidGigs.length} outstanding gig${clientUnpaidGigs.length > 1 ? 's' : ''}.`, margin, infoY + 5);
    
    // Payment info (IBAN) - place after summary with page overflow check
    const ibanHeight = 30;
    const currentY = infoY + 15;
    
    if (user?.iban && user.iban.trim() !== '') {
      if (currentY + ibanHeight > pageHeight - 25) {
        // Not enough space, add new page
        doc.addPage();
        addPaymentInfo(doc, user.iban, 20);
        addFooter(doc, { username: user?.username || 'Unknown' });
      } else {
        // Enough space, add IBAN after info text
        addPaymentInfo(doc, user.iban, currentY);
      }
    }
    
    savePDF(doc, `statement-${filterClient.replace(/[^a-z0-9]/gi, '-').toLowerCase()}-${moment().format('YYYY-MM-DD')}.pdf`);
  };

  const handleClearFilters = () => {
    setFilterClient('');
    setFilterLocation('');
    setFilterPaid('');
    setFilterGigType('');
  };

  const hasActiveFilters = filterClient || filterLocation || filterPaid || filterGigType;

  // Calculate total amount owed to substitutes for unpaid subs
  // Only include gigs where substitute has NOT been paid
  const totalOwed = filteredGigs.reduce((sum, gig) => {
    const gigType = gig.gig_type || (gig.is_substitution === 1 || gig.is_substitution === true ? 'sub_for_other' : 'mine');
    const substitutePaid = gig.substitute_paid === 1 || gig.substitute_paid === true;
    if (gigType === 'sub_for_me' && !substitutePaid && gig.substitute_amount) {
      return sum + parseFloat(gig.substitute_amount);
    }
    return sum;
  }, 0);

  // Calculate total missing amount for unpaid/partially paid gigs (only for unpaid gigs view)
  const totalUnpaidRevenue = filteredGigs.reduce((sum, gig) => {
    const days = moment(gig.end_date).diff(moment(gig.start_date), 'days') + 1;
    const rateType = gig.rate_type || 'daily';
    const revenue = rateType === 'flat' ? parseFloat(gig.daily_rate) : days * parseFloat(gig.daily_rate);
    const paidAmount = gig.paid_amount !== undefined && gig.paid_amount !== null ? parseFloat(gig.paid_amount) : 0;
    const missingAmount = revenue - paidAmount;
    return sum + missingAmount;
  }, 0);

  return (
    <div className="gigs-list-page">
      <div className="gigs-list-header">
        <div>
          <h1>{title || 'All Gigs'}</h1>
          {totalOwed > 0 && (
            <p style={{ color: '#ff9800', marginTop: '0.5rem', fontSize: '0.95rem', fontWeight: 500 }}>
              💰 Total Owed to Substitutes: €{totalOwed.toFixed(2)}
            </p>
          )}
          {title === 'Unpaid Gigs' && totalUnpaidRevenue > 0 && (
            <p style={{ color: '#ff6b6b', marginTop: totalOwed > 0 ? '0.25rem' : '0.5rem', fontSize: '0.95rem', fontWeight: 500 }}>
              💸 Total Amount Owed: €{totalUnpaidRevenue.toFixed(2)}
            </p>
          )}
        </div>
        <div className="gigs-list-actions">
          {onNewGig && (
            <button className="btn btn-primary" onClick={onNewGig}>
              + New Gig
            </button>
          )}
          <button className="btn btn-primary" onClick={exportToPDF}>
            Export Report
          </button>
          {filterClient && (
            <button 
              className="btn btn-secondary" 
              onClick={exportClientStatement}
              style={{ backgroundColor: '#cb6800', borderColor: '#cb6800' }}
            >
              Client Statement
            </button>
          )}
        </div>
      </div>

      <div className="gigs-filters">
        <div className="filter-group">
          <label htmlFor="filter-client">Filter by Client:</label>
          <select
            id="filter-client"
            value={filterClient}
            onChange={(e) => setFilterClient(e.target.value)}
            className="filter-select"
          >
            <option value="">All Clients</option>
            {uniqueClients.map(client => (
              <option key={client} value={client}>{client}</option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label htmlFor="filter-location">Filter by Location:</label>
          <select
            id="filter-location"
            value={filterLocation}
            onChange={(e) => setFilterLocation(e.target.value)}
            className="filter-select"
          >
            <option value="">All Locations</option>
            {uniqueLocations.map(location => (
              <option key={location} value={location}>{location}</option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label htmlFor="filter-paid">Payment Status:</label>
          <select
            id="filter-paid"
            value={filterPaid}
            onChange={(e) => setFilterPaid(e.target.value)}
            className="filter-select"
          >
            <option value="all">All</option>
            <option value="paid">Paid</option>
            <option value="unpaid">Unpaid</option>
          </select>
        </div>

        <div className="filter-group">
          <label htmlFor="filter-gig-type">Gig Type:</label>
          <select
            id="filter-gig-type"
            value={filterGigType}
            onChange={(e) => setFilterGigType(e.target.value)}
            className="filter-select"
          >
            <option value="all">All</option>
            <option value="mine">My Gigs</option>
            <option value="sub_for_other">Substituting for Someone</option>
            <option value="sub_for_me">Someone Substituting for Me</option>
          </select>
        </div>

        {hasActiveFilters && (
          <button className="btn btn-secondary" onClick={handleClearFilters}>
            Clear Filters
          </button>
        )}
      </div>

      {hasActiveFilters && (
        <div className="filter-info">
          Showing {sortedGigs.length} of {gigs.length} gigs
        </div>
      )}

      {sortedGigs.length > 0 ? (
        <div className="gigs-list-container">
          <table className="gigs-table">
            <thead>
              <tr>
                <th onClick={() => handleSort('title')} className="sortable">
                  Title {sortBy === 'title' && (sortOrder === 'asc' ? '↑' : '↓')}
                </th>
                <th onClick={() => handleSort('client')} className="sortable">
                  Client {sortBy === 'client' && (sortOrder === 'asc' ? '↑' : '↓')}
                </th>
                <th>Location</th>
                <th onClick={() => handleSort('start_date')} className="sortable">
                  Start Date {sortBy === 'start_date' && (sortOrder === 'asc' ? '↑' : '↓')}
                </th>
                <th>End Date</th>
                <th>Duration</th>
                <th>Rate</th>
                <th title="Total expenses for this gig">
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                    <span>Expenses</span>
                    <span style={{ fontSize: '0.65rem', color: '#999', fontWeight: 'normal', marginTop: '0.2rem' }}>All costs</span>
                  </div>
                </th>
                <th title="Profit = Revenue - Your expenses (yellow = profit, red = loss)">
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                    <span>Profit</span>
                    <span style={{ fontSize: '0.65rem', color: '#999', fontWeight: 'normal', marginTop: '0.2rem' }}>Revenue - Your costs</span>
                  </div>
                </th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedGigs.map(gig => {
                const days = moment(gig.end_date).diff(moment(gig.start_date), 'days') + 1;
                const rateType = gig.rate_type || 'daily';
                const baseRevenue = rateType === 'flat' ? parseFloat(gig.daily_rate) : days * parseFloat(gig.daily_rate);
                const additionalCharges = gig.additional_charges?.reduce((sum, charge) => sum + parseFloat(charge.amount), 0) || 0;
                const revenue = baseRevenue + additionalCharges;
                const totalExpenses = gig.expenses?.reduce((sum, exp) => sum + parseFloat(exp.amount), 0) || 0;
                const selfExpenses = gig.expenses?.filter(exp => exp.covered_by === 'self').reduce((sum, exp) => sum + parseFloat(exp.amount), 0) || 0;
                const profit = revenue - selfExpenses;

                const isPaid = gig.paid === 1 || gig.paid === true;
                const isCanceled = gig.is_canceled === 1 || gig.is_canceled === true;
                const gigType = gig.gig_type || (gig.is_substitution === 1 || gig.is_substitution === true ? 'sub_for_other' : 'mine');
                const isSubstitution = gigType !== 'mine';
                const isUpcoming = gig.start_date && moment(gig.start_date).startOf('day').isAfter(today);
                
                // Calculate if gig is partially paid
                const paidAmount = gig.paid_amount !== undefined && gig.paid_amount !== null ? parseFloat(gig.paid_amount) : 0;
                const isPartiallyPaid = paidAmount > 0 && paidAmount < revenue && !isPaid;
                
                return (
                  <tr key={gig.id} onClick={() => onSelectGig(gig)} className={`gig-row ${isCanceled ? 'canceled-gig' : isPartiallyPaid ? 'partially-paid-gig' : ''} ${selectedGig && selectedGig.id === gig.id ? 'selected-gig' : ''}`}>
                    <td data-label="Title">
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                          <span>{gig.title}</span>
                          {isUpcoming && (
                            <span className="status-badge-inline upcoming-status">
                              Upcoming
                            </span>
                          )}
                          {isSubstitution && (
                            <span className={`status-badge-inline ${gigType === 'sub_for_other' ? 'substitution-status' : gigType === 'sub_for_me' ? 'sub-for-me-status' : ''}`}>
                              {gigType === 'sub_for_other' ? 'Substituting' : gigType === 'sub_for_me' ? 'Sub for Me' : ''}
                            </span>
                          )}
                        </div>
                        {isPartiallyPaid ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.15rem' }}>
                            <span className={`payment-status-text partially-paid`}>Partially Paid</span>
                            <span style={{ fontSize: '0.75rem', color: '#ff9800', fontWeight: 'normal' }}>
                              missing €{(revenue - paidAmount).toFixed(2)}
                            </span>
                          </div>
                        ) : (
                          <span className={`payment-status-text ${isCanceled && isPaid ? 'canceled-paid' : (isCanceled ? 'canceled' : (isPaid ? 'paid' : 'unpaid'))}`}>
                            {isCanceled && isPaid ? 'Canceled & Paid' : (isCanceled ? 'Canceled (Not Paid)' : (isPaid ? 'Paid' : 'Unpaid'))}
                          </span>
                        )}
                      </div>
                    </td>
                    <td data-label="Client">{gig.client_name || '-'}</td>
                    <td data-label="Location">{gig.venue || '-'}</td>
                    <td data-label="Start Date">{moment(gig.start_date).format('MMM D, YYYY')}</td>
                    <td data-label="End Date">{moment(gig.end_date).format('MMM D, YYYY')}</td>
                    <td data-label="Duration">{rateType === 'flat' ? 'Flat' : `${days} ${days === 1 ? 'day' : 'days'}`}</td>
                    <td data-label="Rate">
                      €{parseFloat(gig.daily_rate).toFixed(2)}
                      <span className="rate-type-badge">{rateType === 'flat' ? ' (flat)' : '/day'}</span>
                    </td>
                    <td className="amount-cell" data-label="Expenses (All costs)">
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                        <span>€{totalExpenses.toFixed(2)}</span>
                        <span style={{ fontSize: '0.65rem', color: '#999', fontWeight: 'normal' }}>All costs</span>
                      </div>
                    </td>
                    <td className={`amount-cell ${profit >= 0 ? 'positive' : 'negative'}`} data-label="Profit (Revenue - Your costs)">
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                        <span>€{profit.toFixed(2)}</span>
                        <span style={{ fontSize: '0.65rem', color: '#999', fontWeight: 'normal' }}>Revenue - Your costs</span>
                      </div>
                    </td>
                    <td data-label="Status">
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', alignItems: 'flex-start' }}>
                        {isUpcoming && <span className="status-badge upcoming-status">Upcoming</span>}
                        {gigType === 'sub_for_other' && <span className="status-badge substitution-status">Substituting</span>}
                        {gigType === 'sub_for_me' && <span className="status-badge sub-for-me-status">Sub for Me</span>}
                        {!isUpcoming && gigType === 'mine' && <span style={{ color: '#999' }}>-</span>}
                      </div>
                    </td>
                    <td onClick={(e) => e.stopPropagation()} className="actions-cell" data-label="">
                      <button
                        className="btn-icon"
                        onClick={() => onEditGig(gig)}
                        title="Edit gig"
                      >
                        Edit
                      </button>
                      <button
                        className="btn-icon"
                        onClick={() => {
                          if (window.confirm('Are you sure you want to delete this gig?')) {
                            onDeleteGig(gig.id);
                          }
                        }}
                        title="Delete gig"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="totals-row">
                <td colSpan="7" className="totals-label" data-label="">Totals:</td>
                <td className="amount-cell totals-amount" data-label="Expenses to collect (Reimbursable)">
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                    <span>€{sortedGigs.reduce((sum, gig) => {
                      const companyExpenses = gig.expenses?.filter(exp => exp.covered_by === 'company').reduce((s, exp) => s + parseFloat(exp.amount), 0) || 0;
                      return sum + companyExpenses;
                    }, 0).toFixed(2)}</span>
                    <span style={{ fontSize: '0.65rem', color: '#999', fontWeight: 'normal' }}>Reimbursable</span>
                  </div>
                </td>
                <td className="amount-cell totals-amount" data-label="Expenses to pay (Out of pocket)">
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                    <span>€{sortedGigs.reduce((sum, gig) => {
                      const selfExpenses = gig.expenses?.filter(exp => exp.covered_by === 'self').reduce((s, exp) => s + parseFloat(exp.amount), 0) || 0;
                      return sum + selfExpenses;
                    }, 0).toFixed(2)}</span>
                    <span style={{ fontSize: '0.65rem', color: '#999', fontWeight: 'normal' }}>Out of pocket</span>
                  </div>
                </td>
                <td className="amount-cell totals-amount" data-label="Profit (Revenue - Your costs)">
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                    <span>€{sortedGigs.reduce((sum, gig) => {
                      const days = moment(gig.end_date).diff(moment(gig.start_date), 'days') + 1;
                      const revenue = gig.rate_type === 'flat' ? gig.daily_rate : days * gig.daily_rate;
                      const selfExpenses = gig.expenses?.filter(exp => exp.covered_by === 'self').reduce((s, exp) => s + parseFloat(exp.amount), 0) || 0;
                      return sum + (revenue - selfExpenses);
                    }, 0).toFixed(2)}</span>
                    <span style={{ fontSize: '0.65rem', color: '#999', fontWeight: 'normal' }}>Revenue - Your costs</span>
                  </div>
                </td>
                <td colSpan="2" data-label=""></td>
              </tr>
            </tfoot>
          </table>
        </div>
      ) : (
        <div className="empty-state">
          <p>No gigs yet. Create your first gig to get started!</p>
        </div>
      )}
    </div>
  );
}

export default GigsList;

